import {
  Box,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';

interface CapabilityOutputOptionsProps {
  aiSettings: {
    includeStrengths: boolean;
    includeLimitations: boolean;
    includeStrategy: boolean;
    includeScales: boolean;
    includeCriteria: boolean;
  };
  handleAiSettingChange: (
    setting: keyof CapabilityOutputOptionsProps['aiSettings']
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  generateCapabilityPitchDeck: boolean;
  setGenerateCapabilityPitchDeck: (val: boolean) => void;
  capabilityPitchTenderContextSlideLength: number;
  setCapabilityPitchTenderContextSlideLength: (val: number) => void;
  capabilityPitchPitchSlideLength: number;
  setCapabilityPitchPitchSlideLength: (val: number) => void;
  capabilityPitchOutputTypes: { pptx: boolean; html: boolean; pdf?: boolean };
  setCapabilityPitchOutputTypes: (val: { pptx: boolean; html: boolean; pdf?: boolean }) => void;
  presentationEngine: 'marp' | 'python-pptx' | 'ppt-master' | 'pure-html';
  setPresentationEngine: (val: 'marp' | 'python-pptx' | 'ppt-master' | 'pure-html') => void;
}

export const CapabilityOutputOptions: React.FC<CapabilityOutputOptionsProps> = ({
  aiSettings,
  handleAiSettingChange,
  generateCapabilityPitchDeck,
  setGenerateCapabilityPitchDeck,
  capabilityPitchTenderContextSlideLength,
  setCapabilityPitchTenderContextSlideLength,
  capabilityPitchPitchSlideLength,
  setCapabilityPitchPitchSlideLength,
  capabilityPitchOutputTypes,
  setCapabilityPitchOutputTypes,
  presentationEngine,
  setPresentationEngine,
}) => {
  return (
    <>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        Capability Analysis Output Options
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={aiSettings.includeStrengths}
              onChange={handleAiSettingChange('includeStrengths')}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">Include Strengths</Typography>
              <Typography variant="body2" color="text.secondary">
                Include a section highlighting potential strengths for the bid.
              </Typography>
            </Box>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={aiSettings.includeLimitations}
              onChange={handleAiSettingChange('includeLimitations')}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">Include Limitations/Risks</Typography>
              <Typography variant="body2" color="text.secondary">
                Include a section highlighting potential limitations, gaps, or risks.
              </Typography>
            </Box>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={aiSettings.includeStrategy}
              onChange={handleAiSettingChange('includeStrategy')}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">Include Bid Strategy</Typography>
              <Typography variant="body2" color="text.secondary">
                Include a section recommending a high-level approach/strategy.
              </Typography>
            </Box>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={aiSettings.includeScales}
              onChange={handleAiSettingChange('includeScales')}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">Include Scoring Scales</Typography>
              <Typography variant="body2" color="text.secondary">
                Include numerical scoring across multiple dimensions (e.g., alignment, risk).
              </Typography>
            </Box>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={aiSettings.includeCriteria}
              onChange={handleAiSettingChange('includeCriteria')}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">Include Evaluation Criteria</Typography>
              <Typography variant="body2" color="text.secondary">
                Break down the capability analysis by specific tender evaluation criteria.
              </Typography>
            </Box>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={
            <Switch
              checked={generateCapabilityPitchDeck}
              onChange={(e) => setGenerateCapabilityPitchDeck(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">Generate Capability Pitch Deck</Typography>
              <Typography variant="body2" color="text.secondary">
                Automatically generate an internal pitch deck after a Capability Analysis completes.
              </Typography>
            </Box>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />

        {generateCapabilityPitchDeck && (
          <Box
            sx={{
              ml: 4,
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <TextField
              select
              label="Presentation Format & Engine"
              variant="outlined"
              size="small"
              value={
                presentationEngine === 'pure-html'
                  ? capabilityPitchOutputTypes?.pdf
                    ? 'pure-html-pdf'
                    : 'pure-html-html'
                  : presentationEngine
              }
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'pure-html-pdf') {
                  setPresentationEngine('pure-html');
                  setCapabilityPitchOutputTypes({
                    ...capabilityPitchOutputTypes,
                    pptx: false,
                    html: false,
                    pdf: true,
                  });
                } else if (val === 'pure-html-html') {
                  setPresentationEngine('pure-html');
                  setCapabilityPitchOutputTypes({
                    ...capabilityPitchOutputTypes,
                    pptx: false,
                    html: true,
                    pdf: false,
                  });
                } else if (val === 'marp') {
                  setPresentationEngine('marp');
                  setCapabilityPitchOutputTypes({
                    ...capabilityPitchOutputTypes,
                    pptx: true,
                    html: false,
                    pdf: false,
                  });
                } else if (val === 'python-pptx') {
                  setPresentationEngine('python-pptx');
                  setCapabilityPitchOutputTypes({
                    ...capabilityPitchOutputTypes,
                    pptx: true,
                    html: false,
                    pdf: false,
                  });
                }
              }}
              FormHelperTextProps={{ sx: { fontSize: '0.875rem' } }}
              helperText="Select the output format and presentation engine."
              sx={{ mb: 1 }}
            >
              <MenuItem value="pure-html-html">
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    HTML
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                    <strong>Best for:</strong> Highest visual quality, fastest generation. Often
                    ready for immediate use. Caveat: Harder to edit. <strong>Cost:</strong> Low
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="pure-html-pdf">
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    HTML (PDF)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                    <strong>Best for:</strong> Fastest generation, digital viewing. Often ready for
                    immediate use (editable with PDF software). <strong>Cost:</strong> Low
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="marp">
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    PPTX (Quality)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                    <strong>Best for:</strong> Highly styled presentations (image-based slides).
                    Will definitely require manual editing. <strong>Cost:</strong> Medium
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="python-pptx">
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    PPTX (Native)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                    <strong>Best for:</strong> Fully editable shapes and texts directly in
                    PowerPoint. Will definitely require manual editing. <strong>Cost:</strong> High
                  </Typography>
                </Box>
              </MenuItem>
            </TextField>

            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 2 }} sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold' }}>
                  {capabilityPitchTenderContextSlideLength + capabilityPitchPitchSlideLength}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Total Slides
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 5 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>{capabilityPitchTenderContextSlideLength}</strong> slides about the
                  tender.
                </Typography>
                <Slider
                  value={capabilityPitchTenderContextSlideLength}
                  min={2}
                  max={10}
                  step={1}
                  onChange={(_, newValue) =>
                    setCapabilityPitchTenderContextSlideLength(newValue as number)
                  }
                  valueLabelDisplay="auto"
                  sx={{
                    height: 6,
                    '& .MuiSlider-thumb': {
                      height: 24,
                      width: 24,
                    },
                    '& .MuiSlider-track': {
                      height: 6,
                    },
                    '& .MuiSlider-rail': {
                      height: 6,
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 5 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>{capabilityPitchPitchSlideLength}</strong> slides about bid strategy.
                </Typography>
                <Slider
                  aria-label="Capability Pitch Slide Length"
                  value={capabilityPitchPitchSlideLength}
                  min={2}
                  max={10}
                  step={1}
                  onChange={(_, newValue) => setCapabilityPitchPitchSlideLength(newValue as number)}
                  valueLabelDisplay="auto"
                  sx={{
                    height: 6,
                    '& .MuiSlider-thumb': {
                      height: 24,
                      width: 24,
                    },
                    '& .MuiSlider-track': {
                      height: 6,
                    },
                    '& .MuiSlider-rail': {
                      height: 6,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </FormGroup>
    </>
  );
};
